import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Users, UserPlus, UserX, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';
import type { Member } from '../../../../../shared/types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost/fhpf-api';

export const MemberManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);

  // Add Member Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUid, setNewUid] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newJoinDate, setNewJoinDate] = useState(new Date().toISOString().substring(0, 10));

  // Exit Member Modal State
  const [exitingMember, setExitingMember] = useState<Member | null>(null);
  const [exitDate, setExitDate] = useState(new Date().toISOString().substring(0, 10));
  const [exitReason, setExitReason] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'members'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: Member[] = [];
        snap.forEach((doc) => list.push(doc.data() as Member));
        list.sort((a, b) => a.name.localeCompare(b.name));
        setMembers(list);
        setLoading(false);
      },
      (err) => {
        console.warn('[FHPF] Members load error:', err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!newUid.trim() || newName.trim().length < 2 || !newEmail.trim()) {
      alert('Please fill in all required member fields.');
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/members`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: newUid.trim(),
          name: newName.trim(),
          email: newEmail.trim(),
          phone: newPhone.trim(),
          joinDate: newJoinDate,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error?.message || 'Failed to add member.');
      }

      setMessage({ type: 'success', text: `New member ${newName} enrolled successfully.` });
      setShowAddModal(false);
      setNewUid('');
      setNewName('');
      setNewEmail('');
      setNewPhone('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to add member.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleExitMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !exitingMember) return;

    if (exitReason.trim().length < 5) {
      alert('Please provide an exit reason with at least 5 characters.');
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/members/${exitingMember.uid}/exit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exitDate,
          exitReason: exitReason.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error?.message || 'Failed to exit member.');
      }

      setMessage({ type: 'success', text: `Member ${exitingMember.name} marked as exited. Future dues stopped.` });
      setExitingMember(null);
      setExitReason('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to exit member.' });
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n: number) => `৳${n.toLocaleString('en-US')}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-500" />
            <span>20-Member Roster & Governance</span>
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Official foundation membership list, enrollment dates, and active/exited standings.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold flex items-center gap-2 shadow-sm shadow-primary-600/20"
        >
          <UserPlus className="w-4 h-4" />
          <span>{t('admin.addMember')}</span>
        </button>
      </div>

      {message && (
        <div
          className={`p-4 rounded-xl border flex items-start gap-2.5 text-xs sm:text-sm ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900'
              : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Member Roster Table */}
      <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-navy-950/40 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <tr>
                <th className="py-3 px-4">Member Name</th>
                <th className="py-3 px-4">Contact</th>
                <th className="py-3 px-4">Join Date</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Total Verified</th>
                <th className="py-3 px-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-navy-800">
              {members.map((m) => (
                <tr key={m.uid} className="hover:bg-gray-50/50 dark:hover:bg-navy-800/40">
                  <td className="py-3 px-4">
                    <div className="font-bold text-gray-900 dark:text-white">{m.name}</div>
                    <div className="font-mono text-[10px] text-gray-400">UID: {m.uid}</div>
                  </td>
                  <td className="py-3 px-4 text-xs text-gray-600 dark:text-gray-300">
                    <div>{m.email}</div>
                    <div>{m.phone}</div>
                  </td>
                  <td className="py-3 px-4 text-xs text-gray-600 dark:text-gray-300">
                    {m.joinDate}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        m.status === 'active'
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                          : 'bg-gray-100 text-gray-700 dark:bg-navy-800 dark:text-gray-400'
                      }`}
                    >
                      {m.status?.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right font-bold text-gray-900 dark:text-white">
                    {fmt(m.approvedTotal || 0)}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {m.status === 'active' ? (
                      <button
                        type="button"
                        onClick={() => setExitingMember(m)}
                        className="p-1.5 rounded-lg border border-red-200 dark:border-red-900/60 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 text-xs font-medium"
                        title="Mark as Exited"
                      >
                        <UserX className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <span className="text-[11px] text-gray-400 italic">Exited ({m.exitDate})</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-navy-900 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-navy-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Enroll New Group Member
            </h3>

            <form onSubmit={handleAddMember} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Firebase Auth UID
                </label>
                <input
                  type="text"
                  required
                  value={newUid}
                  onChange={(e) => setNewUid(e.target.value)}
                  placeholder="Paste user UID from Firebase Console..."
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Full Legal Name
                </label>
                <input
                  type="text"
                  required
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Mohammad Ali"
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="ali@example.com"
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  placeholder="+8801XXXXXXXXX"
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Enrollment Date
                </label>
                <input
                  type="date"
                  required
                  value={newJoinDate}
                  onChange={(e) => setNewJoinDate(e.target.value)}
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-200 dark:border-navy-700 text-gray-600 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-xs font-semibold rounded-xl bg-primary-600 hover:bg-primary-700 text-white"
                >
                  Enroll Member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Exit Member Modal */}
      {exitingMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-navy-900 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-navy-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Mark Member as Exited: {exitingMember.name}
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              This action stops future dues from accumulating after the exit date. All historical contributions and records are permanently preserved in the ledger.
            </p>

            <form onSubmit={handleExitMember} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Exit Effective Date
                </label>
                <input
                  type="date"
                  required
                  value={exitDate}
                  onChange={(e) => setExitDate(e.target.value)}
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Exit Reason / Resolution Notes
                </label>
                <textarea
                  required
                  rows={3}
                  value={exitReason}
                  onChange={(e) => setExitReason(e.target.value)}
                  placeholder="e.g. Relocated abroad, voluntary withdrawal approved by Foundation board..."
                  className="w-full p-3 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setExitingMember(null)}
                  className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-200 dark:border-navy-700 text-gray-600 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-xs font-semibold rounded-xl bg-red-600 hover:bg-red-700 text-white"
                >
                  Confirm Exit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
